import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

/**
 * The prototype's menus are absolutely positioned inside a `position:
 * relative` anchor and close on any document click (the root's onClick) or
 * Escape. `stopPropagation` on the surface keeps clicks inside from closing
 * it. This module reproduces that: a `useDismiss` hook and the surface with
 * its exact box (4px padding, radius 8, 1px #e3e3e1, the menu shadow).
 */
export function useDismiss(open: boolean, onClose: () => void): void {
  useEffect(() => {
    if (!open) return;
    const onDoc = () => onClose();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('click', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);
}

export const stop = (e: React.SyntheticEvent) => e.stopPropagation();

export function MenuAnchor({ children, style, display = 'block' }: { children: ReactNode; style?: CSSProperties; display?: 'block' | 'flex' }) {
  return <div style={{ position: 'relative', display, ...style }}>{children}</div>;
}

/** Space kept between a menu and the edge of the screen, and between a flipped menu and its anchor. */
const EDGE = 8;
const GAP = 4;

export function MenuSurface({
  children,
  width,
  top = 38,
  bottom,
  left,
  right,
  maxHeight,
  padding = 4,
  radius = 8,
  shadow = 'soft',
  zIndex = 30,
  style,
}: {
  children: ReactNode;
  width?: number | string;
  top?: number | string;
  bottom?: number | string;
  left?: number | string;
  right?: number | string;
  maxHeight?: number | string;
  padding?: number | string;
  radius?: number;
  shadow?: 'soft' | 'menu' | 'search';
  zIndex?: number;
  style?: CSSProperties;
}) {
  const box = shadow === 'search' ? 'var(--tr-shadow-search)' : shadow === 'menu' ? 'var(--tr-shadow-menu)' : 'var(--tr-shadow-menu-soft)';
  // KEPT ON SCREEN. A menu is placed against its anchor, so one opened near
  // the right edge (the visibility chip at the end of the viewer's top bar)
  // ran off the screen. Measured once when it opens, then nudged left to fit,
  // and flipped above its anchor when there is no room below.
  const ref = useRef<HTMLDivElement>(null);
  const [nudge, setNudge] = useState<{ x: number; y: number } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;
    let x = 0;
    let y = 0;
    if (r.right > vw - EDGE) x = vw - EDGE - r.right;
    if (r.left + x < EDGE) x = EDGE - r.left;
    if (r.bottom > vh - EDGE) {
      const anchor = (el.offsetParent as HTMLElement | null)?.getBoundingClientRect();
      const above = anchor ? anchor.top - GAP - r.height : -1;
      if (anchor && above >= EDGE) y = above - r.top;
    }
    if (x || y) setNudge({ x, y });
  }, []);
  const transform = [style?.transform, nudge ? `translate(${nudge.x}px, ${nudge.y}px)` : ''].filter(Boolean).join(' ') || undefined;
  return (
    <div
      ref={ref}
      onClick={stop}
      style={{
        position: 'absolute',
        top: bottom === undefined ? top : undefined,
        bottom,
        left,
        right,
        width,
        maxWidth: 'calc(100vw - 16px)',
        maxHeight,
        overflow: maxHeight ? 'auto' : undefined,
        padding,
        background: 'var(--tr-surface)',
        color: 'var(--tr-ink)',
        border: '1px solid var(--tr-line-2)',
        borderRadius: radius,
        boxShadow: box,
        zIndex,
        ...style,
        transform,
      }}
    >
      {children}
    </div>
  );
}

/** 11px/600/.06em #8a8a8f uppercase section label. */
export function MenuLabel({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div style={{ padding: '6px 10px 4px', fontSize: 11, fontWeight: 600, letterSpacing: '.06em', color: 'var(--tr-hint)', ...style }}>
      {children}
    </div>
  );
}

export function MenuDivider() {
  return <div style={{ height: 1, background: 'var(--tr-line)', margin: '4px 6px' }} />;
}

/** 13px item, radius 5, hover #f2f2f0; `danger` makes it #b3261e with the red hover. */
export function MenuItem({
  children,
  onClick,
  icon,
  right,
  danger,
  selected,
  disabled,
  padding = '8px 10px',
  style,
}: {
  children: ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  icon?: ReactNode;
  right?: ReactNode;
  danger?: boolean;
  selected?: boolean;
  disabled?: boolean;
  padding?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className={disabled ? 'tr-hover-none' : danger ? 'tr-hover-danger2' : 'tr-hover-track'}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) onClick?.(e);
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: right ? 'space-between' : undefined,
        gap: 9,
        padding,
        borderRadius: 5,
        fontSize: 13,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        color: danger ? 'var(--tr-danger-2)' : 'var(--tr-ink)',
        background: selected ? 'var(--tr-hover)' : 'transparent',
        ...style,
      }}
    >
      {icon}
      {children}
      {right}
    </div>
  );
}
